import { buildAcmeSearch } from 'utils/acme-utils.js';

/**
 * The client-side equivalent of fetchFilteredInvoices / fetchInvoicesPages.
 *
 * The SpyneJS app holds every invoice and filters the collection in the browser;
 * the Next.js app re-queries Postgres on every keystroke. These functions keep
 * the matching semantics aligned with the SQL rather than doing something
 * merely reasonable.
 *
 * Pure register: input to output, no framework, independently testable.
 * [author-in-correct-register]
 */

// Matches ITEMS_PER_PAGE in server/queries.js and the Next.js app's data.ts.
export const ITEMS_PER_PAGE = 6;

/**
 * Reproduces the WHERE clause:
 *
 *   customers.name    ILIKE %query%  OR
 *   customers.email   ILIKE %query%  OR
 *   invoices.amount::text ILIKE %query%  OR
 *   invoices.date::text   ILIKE %query%  OR
 *   invoices.status   ILIKE %query%
 *
 * Two casts had to be checked against the database rather than guessed, because
 * the JSON shape differs from the SQL cast:
 *
 *   amount::text -> "44800"      amount is stored in CENTS, so a search for
 *                                "448" matches $448.00 and a search for "4.48"
 *                                matches nothing. Same on both sides.
 *   date::text   -> "2023-09-10" no time part, whereas the JSON carries
 *                                "2023-09-10T00:00:00.000Z". Slicing to 10
 *                                chars is what keeps a "2023-09" search
 *                                matching the same rows in both apps.
 *
 * ILIKE is case-insensitive substring; an empty query matches everything, since
 * `%%` matches any non-null value.
 */
export const filterInvoices = (invoices = [], query = '') => {
  const q = String(query).trim().toLowerCase();

  if (q === '') return invoices;

  return invoices.filter((invoice) =>
    [
      invoice.name,
      invoice.email,
      String(invoice.amount),
      String(invoice.date).slice(0, 10),
      invoice.status,
    ].some((field) => String(field).toLowerCase().includes(q)),
  );
};

// ── Sorting ─────────────────────────────────────────────────────────────────
//
// The sortable columns and the value each one orders by. `customer` sorts the
// visible name, not the email; `date` compares the ISO string, which orders
// chronologically without constructing Dates; `amount` is numeric CENTS (see
// filterInvoices). Status is the two literals 'paid'/'pending', so a plain
// string compare is a real ordering: ascending puts paid first.
const SORT_VALUE_BY_KEY = {
  customer: (invoice) => String(invoice.name).toLowerCase(),
  amount: (invoice) => Number(invoice.amount),
  date: (invoice) => String(invoice.date),
  status: (invoice) => String(invoice.status).toLowerCase(),
};

const SORT_DIRS = ['asc', 'desc'];

export const isValidInvoiceSortKey = (key) =>
  Object.hasOwn(SORT_VALUE_BY_KEY, key);

/**
 * Orders a copy of the collection by one of the four sortable columns.
 *
 * A null/unknown key returns the input untouched — the server's newest-first
 * order (`ORDER BY invoices.date DESC, invoices.id DESC`) is the default and
 * this function must not disturb it. Array.prototype.sort is stable, so rows
 * that compare equal (two invoices on the same date, two 'paid' rows) keep
 * that newest-first order among themselves.
 */
export const sortInvoices = (
  invoices = [],
  sortKey = null,
  sortDir = 'asc',
) => {
  const getValue = SORT_VALUE_BY_KEY[sortKey];

  if (getValue == null) return invoices;

  const direction = sortDir === 'desc' ? -1 : 1;

  return [...invoices].sort((a, b) => {
    const aVal = getValue(a);
    const bVal = getValue(b);

    if (aVal < bVal) return -direction;
    if (aVal > bVal) return direction;
    return 0;
  });
};

/**
 * The next `sort` param value after a header click: same column flips the
 * direction, a new column starts ascending. Returns the `<key>-<dir>` string
 * buildAcmeSearch writes, or '' (delete the key) for an invalid column.
 */
export const nextInvoiceSortValue = (currentKey, currentDir, clickedKey) => {
  if (isValidInvoiceSortKey(clickedKey) === false) return '';

  const dir =
    clickedKey === currentKey && currentDir === 'asc' ? 'desc' : 'asc';

  return `${clickedKey}-${dir}`;
};

/**
 * Reads the invoice search term and sort off a query string.
 *
 * The channel never holds them. window.location is the state, so this runs on
 * every params event and the answer is always current — there is no cached copy
 * to fall out of step, and a deeplink is indistinguishable from an edit, which
 * is the point of the loop.
 *
 * `sort` is one param, `<key>-<dir>` (e.g. `sort=amount-desc`), because the
 * two halves are one fact — a direction without a column is meaningless — and
 * one key merges/deletes atomically through buildAcmeSearch. Anything
 * malformed reads as no sort at all: sortKey/sortDir null, default order.
 */
export const readInvoiceParams = (search = '') => {
  const params = new URLSearchParams(search);
  const [sortKey = '', sortDir = ''] = String(params.get('sort') || '').split(
    '-',
  );
  const isValidSort =
    isValidInvoiceSortKey(sortKey) && SORT_DIRS.includes(sortDir);

  return {
    query: params.get('query') || '',
    sortKey: isValidSort ? sortKey : null,
    sortDir: isValidSort ? sortDir : null,
  };
};

/**
 * The inverse of readInvoiceParams: merges an update into the current query
 * string and returns the new one.
 *
 * Merging rather than replacing leaves unrelated URL parameters intact. An
 * empty value DELETES its key, matching search.tsx, which
 * does `if (term) params.set('query', term) else params.delete('query')` so a
 * cleared search box leaves no `?query=` behind.
 *
 * Returns the search string without its leading `?` — URLSearchParams.toString
 * form — because an empty result must produce a bare path rather than a
 * trailing `?`.
 */
export const buildInvoiceSearch = buildAcmeSearch;

/**
 * The custom window event that closes the params loop.
 *
 * history.replaceState and pushState fire nothing — verified, zero popstate
 * events for either — so the view that writes the URL announces the write
 * itself. Every consumer derives from this one string: index.js registers it in
 * config.channels.WINDOW.customEvents, the null view dispatches it, and the
 * channel derives its action label from it the way the framework does
 * (`CHANNEL_WINDOW_${name.toUpperCase()}_EVENT`). Three copies of the literal
 * would be three chances for the loop to open silently.
 */
export const INVOICE_PARAMS_EVENT = 'acme_invoices_params_changed';

/**
 * The row/card breakpoint, matching next-learn's `md`. One definition: index.js
 * registers it under config.channels.WINDOW.mediaQueries, and the invoices
 * channel seeds its own display state from the same string — matchMedia only
 * reports CHANGES, so the boot value has to be read once, and reading a
 * different string than the one registered would let the two drift.
 */
export const INVOICE_MOBILE_QUERY = '(max-width: 768px)';

/**
 * formatCurrency and formatDateToLocal from the Next.js app's lib/utils.ts.
 *
 * The source builds a new Intl formatter on every call — `toLocaleString` does
 * so internally, and formatDateToLocal constructs an Intl.DateTimeFormat per
 * invoice. Constructing an Intl formatter is the expensive part; formatting
 * with one is cheap. Both are hoisted to module scope here and reused across
 * every row, which is the whole difference at list scale. Output is identical.
 */
const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

// Amounts are stored in CENTS — see filterInvoices.
export const formatInvoiceAmount = (amount) =>
  CURRENCY_FORMATTER.format(Number(amount) / 100);

/**
 * The two dashboard card sums, recomputed client-side from the held invoices.
 *
 * Mirrors fetchCardData's SUM(CASE WHEN status = ...) so an optimistic status
 * toggle can keep the cards true before the authoritative dump returns. Output
 * format is byte-identical to the server's formatCurrency: both are
 * en-US/USD currency formatting of cents/100.
 *
 * Pure register: input to output, no framework. [author-in-correct-register]
 */
export const computeInvoiceStatusTotals = (invoices = []) => {
  let paidCents = 0;
  let pendingCents = 0;

  invoices.forEach((invoice) => {
    if (invoice.status === 'paid') paidCents += Number(invoice.amount);
    if (invoice.status === 'pending') pendingCents += Number(invoice.amount);
  });

  return {
    totalPaidInvoices: formatInvoiceAmount(paidCents),
    totalPendingInvoices: formatInvoiceAmount(pendingCents),
  };
};

export const formatInvoiceDate = (dateStr) =>
  DATE_FORMATTER.format(new Date(dateStr));

// ── Live events ─────────────────────────────────────────────────────────────
//
// The client-side half of the live payment simulator. The server commits this
// tick's events and reports them with the full joined row for every touched
// invoice; these functions patch the held dump and recompute every derived
// slice from the patched collection, so one merge keeps the whole dump
// internally consistent — the same recompute-with-the-fact discipline the
// optimistic toggle established in acmeData$ApplyInvoiceStatus.
//
// Pure register throughout: input to output, no framework, independently
// testable. [author-in-correct-register]

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * The server's collection order — ORDER BY invoices.date DESC, invoices.id
 * DESC — as a comparator, so a live-created invoice can be spliced into the
 * held dump at the position a re-fetched dump would give it. The id tiebreak
 * is a plain string compare, matching Postgres's text ordering closely enough
 * for uuids that only ever tie within a single date.
 */
export const compareInvoicesNewestFirst = (a, b) => {
  const aDate = String(a.date);
  const bDate = String(b.date);

  if (aDate !== bDate) return aDate < bDate ? 1 : -1;

  const aId = String(a.id);
  const bId = String(b.id);

  if (aId === bId) return 0;
  return aId < bId ? 1 : -1;
};

/**
 * The latest-invoices slice, recomputed from the held collection — the same
 * five rows fetchLatestInvoices would return (the collection is kept in that
 * query's exact order), with the same server-side currency formatting.
 */
const computeLatestInvoices = (invoices = []) =>
  invoices.slice(0, 5).map(({ id, name, email, image_url, amount }) => ({
    id,
    name,
    email,
    image_url,
    amount: formatInvoiceAmount(amount),
  }));

/**
 * Recomputes the customers-table aggregates for the given customer ids from
 * the held invoices — the client-side mirror of fetchFilteredCustomers's
 * COUNT/SUM, with identical currency formatting. Only touched customers are
 * rebuilt; everyone else keeps their server-computed row.
 */
const patchCustomerAggregates = (
  customers = [],
  invoices = [],
  customerIds,
) => {
  if (customerIds.size === 0) return customers;

  return customers.map((customer) => {
    if (customerIds.has(String(customer.id)) === false) return customer;

    let totalInvoices = 0;
    let pendingCents = 0;
    let paidCents = 0;

    invoices.forEach((invoice) => {
      if (String(invoice.customer_id) !== String(customer.id)) return;

      totalInvoices += 1;
      if (invoice.status === 'pending') pendingCents += Number(invoice.amount);
      if (invoice.status === 'paid') paidCents += Number(invoice.amount);
    });

    return {
      ...customer,
      total_invoices: totalInvoices,
      total_pending: formatInvoiceAmount(pendingCents),
      total_paid: formatInvoiceAmount(paidCents),
    };
  });
};

/**
 * Applies one tick's events to the held dump, immutably.
 *
 *   invoice-paid     the held row takes the server row's status — unless the
 *                    invoice has an in-flight optimistic toggle (skipIds): the
 *                    user's own intent owns that invoice's reconciliation, and
 *                    the toggle's authoritative refetch will settle it.
 *   invoice-created  the server row is spliced in at collection order, unless
 *                    the id is already held (a replayed or journal-reapplied
 *                    event) — the merge is idempotent by construction.
 *
 * Every derived slice then moves WITH the facts: the card sums and counts,
 * the latest-invoices five, the page count, and the touched customers'
 * aggregates are all recomputed from the patched collection.
 *
 * @param {Object} data      the held dump (acmeData$GetData shape)
 * @param {Array}  events    [{ type, invoiceId }]
 * @param {Array}  rows      full joined rows for every invoiceId in events
 * @param {Set}    skipIds   invoice ids with an in-flight optimistic toggle
 * @returns {{ data: Object, changed: Boolean }}
 */
export const applyLiveInvoiceEvents = (
  data,
  events = [],
  rows = [],
  skipIds = new Set(),
) => {
  const rowById = new Map(rows.map((row) => [String(row.id), row]));
  const touchedCustomerIds = new Set();

  let invoices = data.invoices || [];
  let changed = false;

  events.forEach((event) => {
    const id = String(event.invoiceId);
    const row = rowById.get(id);

    if (row == null) return;

    if (event.type === 'invoice-created') {
      if (invoices.some((invoice) => String(invoice.id) === id)) return;

      invoices = [...invoices, row].sort(compareInvoicesNewestFirst);
      touchedCustomerIds.add(String(row.customer_id));
      changed = true;
      return;
    }

    if (event.type === 'invoice-paid') {
      if (skipIds.has(id) === true) return;

      const held = invoices.find((invoice) => String(invoice.id) === id);

      if (held == null || held.status === row.status) return;

      invoices = invoices.map((invoice) =>
        String(invoice.id) === id
          ? { ...invoice, status: row.status }
          : invoice,
      );
      touchedCustomerIds.add(String(row.customer_id));
      changed = true;
    }
  });

  if (changed === false) return { data, changed: false };

  return {
    changed: true,
    data: {
      ...data,
      invoices,
      cards: {
        ...data.cards,
        ...computeInvoiceStatusTotals(invoices),
        numberOfInvoices: invoices.length,
      },
      latestInvoices: computeLatestInvoices(invoices),
      totalPages: Math.ceil(invoices.length / ITEMS_PER_PAGE),
      customers: patchCustomerAggregates(
        data.customers,
        invoices,
        touchedCustomerIds,
      ),
    },
  };
};

/**
 * Conforms one tick's raw events into feed entries. The human-readable line is
 * composed HERE, channel-side, so every payload the feed view receives already
 * carries its msg — the view only renders. [record:derived-activity-log]
 */
export const buildLiveFeedEvents = (events = [], rows = []) => {
  const rowById = new Map(rows.map((row) => [String(row.id), row]));

  return events
    .map((event) => {
      const row = rowById.get(String(event.invoiceId));

      if (row == null) return null;

      const amount = formatInvoiceAmount(row.amount);
      const isPaid = event.type === 'invoice-paid';

      return {
        eventId: event.id,
        type: event.type,
        invoiceId: String(event.invoiceId),
        msg: isPaid
          ? `${row.name} paid ${amount}`
          : `New invoice for ${row.name} — ${amount}`,
        time: TIME_FORMATTER.format(new Date(event.at)),
        name: row.name,
        image_url: row.image_url,
      };
    })
    .filter((entry) => entry !== null);
};

// ── Bulk edit ───────────────────────────────────────────────────────────────
//
// The client-side half of the bulk-editable table. A draft is an UNSAVED local
// edit: { amount? (cents), date? ('YYYY-MM-DD'), status? }, keyed by invoice
// id, holding only the fields that differ from the held dump. Drafts are an
// OVERLAY — the held dump stays the external truth (live ticks keep patching
// it underneath), and these functions compose the two for display or fold a
// confirmed save back into the dump.
//
// Pure register throughout: input to output, no framework, independently
// testable. [author-in-correct-register]

export const EDITABLE_INVOICE_FIELDS = ['amount', 'date', 'status'];

/** The held dump's value for one editable field, in draft units. */
export const readInvoiceField = (invoice, field) => {
  if (invoice == null) return undefined;
  if (field === 'amount') return Number(invoice.amount);
  if (field === 'date') return String(invoice.date).slice(0, 10);
  return invoice.status;
};

/**
 * Overlays unsaved drafts onto a copy of the collection for DISPLAY — the
 * table's row map renders from this, so a row minted mid-session shows the
 * draft values. Untouched invoices pass through by reference; edited ones are
 * rebuilt with the drafted fields and flagged isEdited. Search, sort and every
 * derived slice keep reading the un-overlaid dump: an unsaved edit changes
 * what the cells SHOW, never what the app has agreed is true.
 */
export const applyInvoiceDrafts = (invoices = [], draftsById = {}) =>
  invoices.map((invoice) => {
    const draft = draftsById[String(invoice.id)];

    if (draft == null) return invoice;

    return {
      ...invoice,
      ...(draft.amount !== undefined ? { amount: draft.amount } : {}),
      ...(draft.date !== undefined
        ? { date: `${draft.date}T00:00:00.000Z` }
        : {}),
      ...(draft.status !== undefined ? { status: draft.status } : {}),
      isEdited: true,
    };
  });

/**
 * Folds a set of CONFIRMED field patches into the held dump — the moment a
 * batch save returns, before the authoritative refetch lands. Same
 * recompute-with-the-fact discipline as applyLiveInvoiceEvents: the collection
 * is re-sorted (a changed date moves a row's collection position) and every
 * derived slice moves with it, so the dump stays internally consistent for
 * the window until the bootstrap confirms it.
 *
 * @param {Object} data     the held dump (acmeData$GetData shape)
 * @param {Object} patches  invoiceId -> { amount? (cents), date? (YYYY-MM-DD),
 *                          status? }
 */
export const applyInvoiceFieldPatches = (data, patches = {}) => {
  const ids = Object.keys(patches);

  if (ids.length === 0) return { data, changed: false };

  const touchedCustomerIds = new Set();

  const invoices = (data.invoices || [])
    .map((invoice) => {
      const patch = patches[String(invoice.id)];

      if (patch == null) return invoice;

      touchedCustomerIds.add(String(invoice.customer_id));

      return {
        ...invoice,
        ...(patch.amount !== undefined ? { amount: patch.amount } : {}),
        ...(patch.date !== undefined
          ? { date: `${patch.date}T00:00:00.000Z` }
          : {}),
        ...(patch.status !== undefined ? { status: patch.status } : {}),
      };
    })
    .sort(compareInvoicesNewestFirst);

  return {
    changed: true,
    data: {
      ...data,
      invoices,
      cards: {
        ...data.cards,
        ...computeInvoiceStatusTotals(invoices),
        numberOfInvoices: invoices.length,
      },
      latestInvoices: computeLatestInvoices(invoices),
      totalPages: Math.ceil(invoices.length / ITEMS_PER_PAGE),
      customers: patchCustomerAggregates(
        data.customers,
        invoices,
        touchedCustomerIds,
      ),
    },
  };
};

const FEED_ROW_BASE = 'flex flex-row items-center justify-between py-3';

const FEED_PILL_BY_TYPE = {
  'invoice-paid': { label: 'Paid', variant: 'bg-green-500 text-white' },
  'invoice-created': { label: 'New', variant: 'bg-gray-100 text-gray-500' },
};

/**
 * Shapes conformed feed entries into what the activity-feed row template
 * renders — the same finished-array discipline as buildInvoiceRows: image
 * path, alt text, pill classes and the border class are all decided here.
 * [shape-data-for-logicless-template]
 */
export const buildActivityFeedRows = (feedEvents = []) =>
  feedEvents.map((event, i) => {
    const pill =
      FEED_PILL_BY_TYPE[event.type] ?? FEED_PILL_BY_TYPE['invoice-created'];

    return {
      attrEventId: event.eventId,
      attrRowClass: i === 0 ? FEED_ROW_BASE : `${FEED_ROW_BASE} border-t`,
      attrImageSrc: 'imgs' + event.image_url,
      attrImageAlt: `${event.name}'s profile picture`,
      msg: event.msg,
      time: event.time,
      pillLabel: pill.label,
      attrPillClass: `${STATUS_PILL_BASE} ${pill.variant}`,
    };
  });

const STATUS_PILL_BASE =
  'inline-flex items-center rounded-full px-2 py-1 text-xs';

const STATUS_PILL_VARIANT = {
  pending: 'bg-gray-100 text-gray-500',
  paid: 'bg-green-500 text-white',
};

/**
 * The status pill's classes, resolved from the status.
 *
 * Exported because the pill now has two renderers — InvoicesStatusView for a
 * single pill, and the bulk row template for a whole table — and a table whose
 * pills drift from the component's pills would read as a bug in one of them.
 */
export const getInvoiceStatusClass = (status) =>
  `${STATUS_PILL_BASE} ${STATUS_PILL_VARIANT[status] ?? STATUS_PILL_VARIANT.pending}`;

/**
 * Shapes raw invoice records into what the bulk row template renders.
 *
 * Every decision is made here and the template iterates a finished array: the
 * image path, the alt text, both formatted values, the pill classes, and the
 * status branch as an object section — exactly one of `isPaid` / `isPending` is
 * present, so the template needs no conditional syntax it does not have.
 * [shape-data-for-logicless-template] [conditional-via-object-section]
 *
 * Keys landing in attributes carry the `attr` prefix.
 * [attr-prefix-for-attribute-placeholders]
 *
 * The seed stores `/customers/<name>.png`, which Next.js resolves against
 * public/. This app has no public root, so the path is prefixed with `imgs` —
 * the convention that resolves against IMG_PATH.
 *
 * ── Why the icons ride every row ────────────────────────────────────────────
 *
 * They were first passed once at the ROOT of the template data, on the reading
 * that outer-scope properties stay reachable inside a section. Measured here:
 * they do not — every `{{svg*}}` inside `{{#invoices}}` rendered EMPTY while
 * every per-row key rendered correctly, and the status icons nested one level
 * deeper failed the same way. So each row carries its own reference.
 *
 * That costs nothing that matters: `icons` holds four strings built once by the
 * caller, and a row copies POINTERS to them, not the strings. The status icon
 * sits inside the isPaid/isPending object because that object is the scope the
 * section renders in — the shape InvoicesStatusView already uses.
 */
export const buildInvoiceRows = (invoices = [], icons = {}) =>
  invoices.map((invoice) => {
    const isPaid = invoice.status === 'paid';

    return {
      name: invoice.name,
      email: invoice.email,
      amount: formatInvoiceAmount(invoice.amount),
      date: formatInvoiceDate(invoice.date),
      // The raw editable values, riding the row so an in-place editor seeds
      // itself from what the cell is showing — dollars for the amount input,
      // the date input's own YYYY-MM-DD, the status literal — plus the
      // unsaved-edit flag applyInvoiceDrafts stamped.
      rawAmount: Number(invoice.amount) / 100,
      rawDate: String(invoice.date).slice(0, 10),
      status: invoice.status,
      isEdited: invoice.isEdited === true,
      attrImageSrc: 'imgs' + invoice.image_url,
      attrImageAlt: `${invoice.name}'s profile picture`,
      attrInvoiceId: invoice.id,
      attrEditHref: `/dashboard/invoices/${invoice.id}/edit`,
      attrStatusClass: getInvoiceStatusClass(isPaid ? 'paid' : 'pending'),
      svgPencil: icons.svgPencil,
      svgTrash: icons.svgTrash,
      ...(isPaid
        ? { isPaid: { label: 'Paid', svgCheck: icons.svgCheck } }
        : { isPending: { label: 'Pending', svgClock: icons.svgClock } }),
    };
  });
